import type { ComponentConfig } from "@puckeditor/core"
import { SectionBase, sectionStyle } from "../lib/section"

export type CTAProps = SectionBase & {
  title: string
  subtitle: string
  /** Primary CTA: label; href is a storefront-relative path (e.g. "/store") */
  ctaLabel: string
  ctaHref: string
  ctaSecondaryLabel: string
  ctaSecondaryHref: string
}

export const CTA: ComponentConfig<CTAProps> = {
  fields: {
    title: { type: "textarea", contentEditable: true },
    subtitle: { type: "textarea", contentEditable: true },
    ctaLabel: { type: "text", label: "Primary CTA", contentEditable: true },
    ctaHref: { type: "text", label: "Primary CTA link" },
    ctaSecondaryLabel: { type: "text", label: "Secondary CTA", contentEditable: true },
    ctaSecondaryHref: { type: "text", label: "Secondary CTA link" },
    background: {
      type: "radio",
      options: [
        { value: "none", label: "Transparent" },
        { value: "muted", label: "Muted" },
        { value: "accent", label: "Dark" },
      ],
    },
    paddingY: {
      type: "radio",
      options: [
        { value: "none", label: "None" },
        { value: "sm", label: "Small" },
        { value: "md", label: "Medium" },
        { value: "lg", label: "Large" },
      ],
    },
  },

  defaultProps: {
    title: "Ready to get started?",
    subtitle: "Join thousands of customers already using our product.",
    ctaLabel: "Start now",
    ctaHref: "/store",
    ctaSecondaryLabel: "",
    ctaSecondaryHref: "",
    background: "accent",
    paddingY: "lg",
  },

  render: ({ title, subtitle, ctaLabel, ctaHref, ctaSecondaryLabel, ctaSecondaryHref, background, paddingY }) => {
    const s = sectionStyle({ background, paddingY })
    const dark = background === "accent"

    return (
      <section style={s}>
        <div style={{ maxWidth: 800, margin: "0 auto", padding: "0 24px", textAlign: "center" }}>
          <h2 style={{ fontSize: "clamp(1.75rem,4vw,2.5rem)", fontWeight: 800, marginBottom: 16, lineHeight: 1.2 }}>{title}</h2>
          {subtitle && <p style={{ fontSize: "1.125rem", color: dark ? "rgba(255,255,255,0.75)" : "#6b7280", marginBottom: 32 }}>{subtitle}</p>}
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            {ctaLabel && (
              ctaHref ? (
                <a
                  href={ctaHref}
                  style={{
                    padding: "12px 32px",
                    background: dark ? "#fff" : "var(--site-accent, #7c3aed)",
                    color: dark ? "#111827" : "#fff",
                    borderRadius: 8,
                    fontSize: 16,
                    fontWeight: 600,
                    textDecoration: "none",
                  }}
                >
                  {ctaLabel}
                </a>
              ) : (
                <button
                  style={{
                    padding: "12px 32px",
                    background: dark ? "#fff" : "var(--site-accent, #7c3aed)",
                    color: dark ? "#111827" : "#fff",
                    border: "none",
                    borderRadius: 8,
                    fontSize: 16,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  {ctaLabel}
                </button>
              )
            )}
            {ctaSecondaryLabel && (
              ctaSecondaryHref ? (
                <a
                  href={ctaSecondaryHref}
                  style={{
                    padding: "12px 24px",
                    color: "inherit",
                    border: dark ? "1px solid rgba(255,255,255,0.4)" : "1px solid #d1d5db",
                    borderRadius: 8,
                    fontSize: 16,
                    fontWeight: 600,
                    textDecoration: "none",
                  }}
                >
                  {ctaSecondaryLabel}
                </a>
              ) : (
                <button
                  style={{
                    padding: "12px 24px",
                    background: "transparent",
                    color: "inherit",
                    border: dark ? "1px solid rgba(255,255,255,0.4)" : "1px solid #d1d5db",
                    borderRadius: 8,
                    fontSize: 16,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  {ctaSecondaryLabel}
                </button>
              )
            )}
          </div>
        </div>
      </section>
    )
  },
}
